import type { AgentCardChild, AgentCardElement } from '@novu/react';
import { toSafeExternalUrl } from '@/utils/url';

export type CardButtonView = { id: string; label: string; value?: string; style?: string };

export type CardChildView =
  | { type: 'text'; content: string }
  | { type: 'divider' }
  | { type: 'image'; url: string; alt: string }
  | { type: 'link'; url: string; label: string }
  | { type: 'actions'; buttons: CardButtonView[] };

export type CardView = {
  title?: string;
  subtitle?: string;
  imageUrl?: string;
  children: CardChildView[];
};

function linkView(label: string, url: string): CardChildView | null {
  const safeUrl = toSafeExternalUrl(url);
  const trimmedLabel = label.trim();

  return safeUrl && trimmedLabel ? { type: 'link', url: safeUrl, label: trimmedLabel } : null;
}

function viewsFromActions(children: Extract<AgentCardChild, { type: 'actions' }>['children']): CardChildView[] {
  const views: CardChildView[] = [];
  const buttons: CardButtonView[] = [];

  for (const actionChild of children) {
    if (actionChild.type === 'button') {
      buttons.push({
        id: actionChild.id,
        label: actionChild.label,
        value: actionChild.value,
        style: actionChild.style,
      });
      continue;
    }

    if (actionChild.type === 'link-button') {
      const view = linkView(actionChild.label, actionChild.url);
      if (view) {
        views.push(view);
      }
    }
  }

  if (buttons.length > 0) {
    views.push({ type: 'actions', buttons });
  }

  return views;
}

function viewsFromAgentChild(child: AgentCardChild): CardChildView[] {
  switch (child.type) {
    case 'text': {
      const content = child.content.trim();

      return content ? [{ type: 'text', content }] : [];
    }
    case 'divider':
      return [{ type: 'divider' }];
    case 'image': {
      const url = toSafeExternalUrl(child.url);

      return url ? [{ type: 'image', url, alt: child.alt ?? '' }] : [];
    }
    case 'link': {
      const view = linkView(child.label, child.url);

      return view ? [view] : [];
    }
    case 'button':
      return [
        {
          type: 'actions',
          buttons: [{ id: child.id, label: child.label, value: child.value, style: child.style }],
        },
      ];
    case 'actions':
      return viewsFromActions(child.children);
    case 'section':
      return child.children.flatMap((nested) => viewsFromAgentChild(nested));
    case 'fields':
      return child.children
        .map((field) => `${field.label}: ${field.value}`.trim())
        .filter(Boolean)
        .map((content) => ({ type: 'text' as const, content }));
    default:
      return [];
  }
}

export function cardViewFromElement(card: AgentCardElement): CardView {
  const children = Array.isArray(card.children) ? card.children : [];

  return {
    title: card.title?.trim() || undefined,
    subtitle: card.subtitle?.trim() || undefined,
    imageUrl: toSafeExternalUrl(card.imageUrl),
    children: children.flatMap((child) => viewsFromAgentChild(child)),
  };
}
